import { createSlice,createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { GiftBrand } from "../models/GiftBrand";
import axios from "../utils/axios";
import { Voucher } from "../models/Voucher";

interface GiftBrandState {
  giftBrands: GiftBrand[];
  myPurchases: GiftBrand[];
  vouchers: Voucher[];
  selectedGiftBrand: GiftBrand | null;
    loading: boolean;
    error: string | null;
    selectGiftCardsDrawerOpen: boolean;
    registeredWebhooks?: any[];
}

interface CountryBrandsResponse {
  country: string;
  countryCode: string;
  brands: GiftBrand[];
  currentPage: number;
  totalCount: number;
  
}





const initialState: GiftBrandState = {
    giftBrands: [],
    selectedGiftBrand: null,
    loading: false,
    error: null,
    vouchers: [],
    selectGiftCardsDrawerOpen: false,
    myPurchases: [],
    registeredWebhooks: [],
    };


export const fetchGiftBrands = createAsyncThunk(
    "giftBrands/fetchGiftBrands",
    async ({ countryCode, currentPage }: { countryCode: string; currentPage: number }) => {      
      

        const response = await axios.get("api/services/app/GiftCard/GetGiftBrands", {
            params: {
                countryCode,
                currentPage,
            }
            });
        if (!response) {
            throw new Error("Failed to fetch gift brands");
        }
        
        return response.data.result;
    }
);

export const fetchAllMyGiftCards = createAsyncThunk(
    "giftBrands/fetchAllMyGiftCards",   
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get("api/services/app/GiftCard/GetAllMyCards");
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const makePaymentForGiftCard = createAsyncThunk(
    "giftBrands/makePaymentForGiftCard",
    async ({ price, productId }: { price: number; productId: number }) => {
        const response = await axios.post("api/services/app/GiftCard/UserPurchaseGiftCard", {
            price,
            productId,
        });
        return response.data.result;
    }
);

export const getRegisteredWebhookUrl = createAsyncThunk(
    "giftBrands/getRegisteredWebhookUrl",
    async () => {
        const response = await axios.get("api/services/app/GiftCard/GetWebhooks");
        return response.data.result;
    }
);

export const createGiftCardsWebhook = createAsyncThunk(
    "giftBrands/createGiftCardsWebhook",
    async ({ url,authorizationHeaderName}: { url: string, authorizationHeaderName: string }) => {
        const response = await axios.post("api/services/app/GiftCard/CreateGiftCardsWebhook", {
            url,
            authorizationHeaderName
        });
        return response.data.result;
    }
);

const giftBrandsSlice = createSlice({
    name: "giftBrands",
    initialState,
    reducers: {
        setGiftBrands: (state, action) => {
            state.giftBrands = action.payload;
        },
        clearGiftBrands: (state) => {
            state.giftBrands = [];
        },
        setSelectedGiftBrand: (state: GiftBrandState, action: PayloadAction<{brand:GiftBrand}>) => {
            state.selectedGiftBrand = action.payload.brand;
        },setSelectGiftCardsDrawerOpen: (state, action: PayloadAction<boolean>) => {
            state.selectGiftCardsDrawerOpen = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGiftBrands.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGiftBrands.fulfilled, (state, action: PayloadAction<CountryBrandsResponse>) => {
                state.loading = false;
                state.giftBrands = action.payload.brands.map((brand: GiftBrand) => ({
                    ...brand,
                    denominations: brand.denominations.length === 0 ? [10, 15, 20, 25, 50, 100] : brand.denominations, // Default denominations
                }));
            })
            .addCase(fetchGiftBrands.rejected, (state, action) => {
                state.loading = false;
                state.giftBrands = [];
                state.error = action.error.message || "Failed to fetch gift brands";
            })
            .addCase(fetchAllMyGiftCards.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllMyGiftCards.fulfilled, (state, action: PayloadAction<Voucher[]>) => {
                state.loading = false;
                state.vouchers = action.payload;
            })
            .addCase(fetchAllMyGiftCards.rejected, (state, action) => {
                state.loading = false;
                state.vouchers = [];
                state.error = action.error.message || "Failed to fetch gift cards";
            })
            .addCase(getRegisteredWebhookUrl.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.registeredWebhooks = action.payload;
            })
            .addCase(getRegisteredWebhookUrl.rejected, (state, action) => {
                state.error = action.error.message || "Failed to fetch registered webhooks";
            })
            .addCase(getRegisteredWebhookUrl.pending, (state) => {
                state.error = null;
            })
            .addCase(createGiftCardsWebhook.fulfilled, (state, action: PayloadAction<any>) => {
                if (state.registeredWebhooks) {
                    state.registeredWebhooks.push(action.payload);
                }
            })
            .addCase(createGiftCardsWebhook.rejected, (state, action) => {
                state.error = action.error.message || "Failed to create webhook";
            })
            .addCase(createGiftCardsWebhook.pending, (state) => {
                state.error = null;
            });
            
    },
});


export const { setGiftBrands, clearGiftBrands,setSelectGiftCardsDrawerOpen } = giftBrandsSlice.actions;
export default giftBrandsSlice.reducer;